import { __ } from '@wordpress/i18n';
import { setupLocationVisibility } from './cityFilter';
import { initMembershipSection } from './membershipRepeater';

const GROUPS = [
	{
		label: __( 'Basic information', 'anacrm' ),
		description: __(
			'Description, gender, DNI, birh year, work condition, sector, keywords.',
			'anacrm'
		),
		open: false,
		fields: [
			'_pe_description',
			'_pe_gender',
			'_pe_dni',
			'_pe_year_birth',
			'_pe_work_condition',
			'_pe_sector',
			'_pe_keyword',
		],
	},
	{
		label: __( 'Location', 'anacrm' ),
		description: __( 'Territory, city.', 'anacrm' ),
		fields: [ '_pe_territory', '_pe_region', '_pe_city' ],
	},
	{
		label: __( 'Interests', 'anacrm' ),
		description: __( 'ESS, permission, work scope.', 'anacrm' ),
		fields: [ '_pe_interest', '_pe_permission_contact', '_pe_work_scope' ],
	},
	{
		label: __( 'Contact', 'anacrm' ),
		description: __( 'Email, phone, website, Social networks.', 'anacrm' ),
		fields: [
			'_pe_contact_mail',
			'_pe_contact_phone',
			'_pe_contact_website',
			'_pe_contact_social',
		],
	},
	{
		label: __( 'Relations', 'anacrm' ),
		description: __(
			'Relations with organizations, spaces, projects, resources and activities.',
			'anacrm'
		),
		fields: [
			'_pe_project',
			'_pe_space',
			'_pe_resource',
			'_pe_activity_registered',
		],
	},
	{
		label: __( 'Temporary fields from the bulk import', 'anacrm' ),
		description: __(
			'Raw organisation/administration data captured during bulk import, kept until it is reviewed and migrated into the proper relation fields.',
			'anacrm'
		),
		temporary: true,
		fields: [
			'_pe_administration_import',
			'_pe_administration_position_import',
			'_pe_administration_department_import',
			'_pe_org_import',
			'_pe_org_position_import',
		],
	},
];

function findFieldWrapper( form, name ) {
	// Pods adds class pods-form-ui-row-name-{name} to every field container.
	// Underscores in the field name are converted to dashes in the class,
	// so _pe_gender → pods-form-ui-row-name--pe-gender (double dash from leading _).
	const dashedName = name.replace( /_/g, '-' );
	return form.querySelector( `.pods-form-ui-row-name-${ dashedName }` );
}

function setupFullName( form ) {
	// Attach to the form immediately — the inputs don't need to exist yet.
	// Every handler invocation queries the DOM fresh, so stale references
	// are never an issue even if React re-renders the nodes later.
	form.addEventListener( 'input', ( e ) => {
		const name = e.target.getAttribute( 'name' ) || '';
		if (
			! name.startsWith( 'pods_field__pe_firstname' ) &&
			! name.startsWith( 'pods_field__pe_lastname' )
		) {
			return;
		}
		const titleInput = form.querySelector(
			'input[name^="pods_field_post_title"]'
		);
		const firstInput = form.querySelector(
			'input[name^="pods_field__pe_firstname"]'
		);
		const lastInput = form.querySelector(
			'input[name^="pods_field__pe_lastname"]'
		);
		if ( ! titleInput || ! firstInput || ! lastInput ) {
			return;
		}
		titleInput.value = [ firstInput.value.trim(), lastInput.value.trim() ]
			.filter( Boolean )
			.join( ' ' );
	} );
}

function setupRequiredNameValidation( form ) {
	let errorEl = null;

	function showError() {
		if ( errorEl ) {
			return;
		}
		errorEl = document.createElement( 'div' );
		errorEl.className = 'w-full px-4 py-3 mb-4 bg-red text-black';
		errorEl.setAttribute( 'role', 'alert' );
		errorEl.textContent = __(
			'Please provide a first name or a last name.',
			'anacrm'
		);
		form.insertBefore( errorEl, form.firstChild );
	}

	function clearError() {
		if ( errorEl ) {
			errorEl.remove();
			errorEl = null;
		}
	}

	function isValid() {
		const firstInput = form.querySelector(
			'input[name^="pods_field__pe_firstname"]'
		);
		const lastInput = form.querySelector(
			'input[name^="pods_field__pe_lastname"]'
		);
		if ( ! firstInput || ! lastInput ) {
			return true;
		}
		return Boolean( firstInput.value.trim() || lastInput.value.trim() );
	}

	function block( e ) {
		e.preventDefault();
		e.stopPropagation();
		e.stopImmediatePropagation();
		showError();
		form.querySelector(
			'input[name^="pods_field__pe_firstname"]'
		)?.focus();
	}

	form.addEventListener( 'input', ( e ) => {
		const name = e.target.getAttribute( 'name' ) || '';
		if (
			name.startsWith( 'pods_field__pe_firstname' ) ||
			name.startsWith( 'pods_field__pe_lastname' )
		) {
			clearError();
		}
	} );

	// Both listeners run in the capture phase so they run — and can block
	// submission — before Pods' own bubble-phase handlers (jquery.pods.js)
	// kick in. Neither firstname nor lastname is individually "required" at
	// the Pods level (a person may be known by only one of them), so Pods'
	// built-in client-side required check never fires for this rule; the
	// equivalent server-side guard in inc/pods.php (pods_api_pre_save_pod_item)
	// does stop the save even if this is bypassed, but doesn't surface a
	// clean error through Pods' front-end AJAX handler, so it must also be
	// caught here.
	//
	// Pods binds its submit-button click handler with jQuery delegation in
	// the bubble phase, and that handler preventDefaults the click and
	// replays it as a synthetic jQuery-only "submit" trigger — which never
	// reaches a real native "submit" event, so a capture-phase "submit"
	// listener alone never sees a button-triggered submission. Intercepting
	// "click" on the button in capture phase stops it before Pods' delegated
	// handler (bubble phase, same form) ever runs. The "submit" listener
	// stays as a fallback for other ways to submit (e.g. pressing Enter in a
	// text field), which do dispatch a real native "submit" event.
	form.addEventListener(
		'click',
		( e ) => {
			if (
				! e.target.closest(
					'input[type="submit"], button[type="submit"]'
				)
			) {
				return;
			}
			if ( isValid() ) {
				clearError();
				return;
			}
			block( e );
		},
		true
	);

	form.addEventListener(
		'submit',
		( e ) => {
			if ( isValid() ) {
				clearError();
				return;
			}
			block( e );
		},
		true
	);
}

function buildAccordion( form ) {
	for ( const group of GROUPS ) {
		const fieldWrappers = group.fields
			.map( ( f ) => findFieldWrapper( form, f ) )
			.filter( Boolean );
		if ( ! fieldWrappers.length ) {
			continue;
		}

		const parent = fieldWrappers[ 0 ].parentElement;

		const details = document.createElement( 'details' );
		if ( group.open ) {
			details.open = true;
		}
		details.className = group.temporary
			? 'mb-4 border-2 border-dashed border-blue'
			: 'mb-4 border-2 border-green';

		const summary = document.createElement( 'summary' );
		summary.className = group.temporary
			? 'px-4 py-3 cursor-pointer select-none bg-blue/10 hover:bg-blue/20 marker:float-left'
			: 'px-4 py-3 cursor-pointer select-none bg-green/30 hover:bg-green/60 marker:float-left';
		const summaryTit = document.createElement( 'h2' );
		summaryTit.className = 'text-base font-semibold inline-block';
		summaryTit.textContent = group.label;
		summary.appendChild( summaryTit );
		const summaryDesc = document.createElement( 'p' );
		summaryDesc.textContent = group.description ?? '';
		summary.appendChild( summaryDesc );
		details.appendChild( summary );

		const body = document.createElement( 'div' );
		body.className = group.temporary
			? 'px-4 py-2 bg-blue/5'
			: 'px-4 py-2 bg-green/10';
		details.appendChild( body );

		parent.insertBefore( details, fieldWrappers[ 0 ] );
		fieldWrappers.forEach( ( w ) => body.appendChild( w ) );
	}
}

document.addEventListener( 'DOMContentLoaded', () => {
	const wrapper = document.querySelector( '.anacrm-person-form-accordion' );
	if ( ! wrapper ) {
		return;
	}

	const successDiv = wrapper.querySelector( '.pods-form-front-success' );
	if ( successDiv ) {
		const btn = document.createElement( 'button' );
		btn.type = 'button';
		btn.className = 'px-3 py-2 text-2xl hover:text-yellow cursor-pointer';
		btn.innerHTML = '&times;';
		btn.addEventListener( 'click', () => successDiv.remove() );
		successDiv.appendChild( btn );
	}

	const form = wrapper.querySelector( 'form' );
	if ( ! form ) {
		return;
	}

	// Full-name sync: attach immediately; the handler queries inputs on each keystroke.
	setupFullName( form );
	setupRequiredNameValidation( form );

	let done = false;

	function tryBuild() {
		if ( done ) {
			return;
		}
		if ( ! form.querySelector( '.pods-field__container' ) ) {
			return;
		}
		done = true;
		observer.disconnect();
		buildAccordion( form );
		setupLocationVisibility( form, {
			territoryField: '_pe_territory',
			regionField: '_pe_region',
			cityField: '_pe_city',
		} );

		// Single membership accordion block — must sit inside .pods-submittable-fields
		// so the hidden inputs are included in Pods' AJAX serialisation.
		const personId = wrapper.dataset.personId ?? '';
		const isNew = wrapper.dataset.isEdit === '0';

		const details = document.createElement( 'details' );
		details.className = 'mb-4 border-2 border border-green';

		const summary = document.createElement( 'summary' );
		summary.className =
			'px-4 py-3 cursor-pointer select-none bg-green/30 hover:bg-green/60 marker:float-left';
		const summaryTit = document.createElement( 'h2' );
		summaryTit.className = 'text-base font-semibold inline-block';
		summaryTit.textContent = __(
			'Membership in organisations and administrations',
			'anacrm'
		);
		summary.appendChild( summaryTit );
		const summaryDesc = document.createElement( 'p' );
		summaryDesc.textContent = '';
		summary.appendChild( summaryDesc );
		details.appendChild( summary );

		const body = document.createElement( 'div' );
		body.className = 'px-4 py-2 bg-green/10';
		details.appendChild( body );

		const submittableFields = form.querySelector(
			'.pods-submittable-fields'
		);
		const submitRow = submittableFields
			? submittableFields.querySelector( '.pods-submit' )
			: null;
		if ( submitRow ) {
			submittableFields.insertBefore( details, submitRow );
		} else if ( submittableFields ) {
			submittableFields.appendChild( details );
		} else {
			form.appendChild( details );
		}

		const orgContainer = document.createElement( 'div' );
		orgContainer.className = 'mb-4';
		const orgHeading = document.createElement( 'p' );
		orgHeading.className =
			'text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2';
		orgHeading.textContent = __( 'Organisations', 'anacrm' );
		orgContainer.appendChild( orgHeading );
		body.appendChild( orgContainer );

		const adContainer = document.createElement( 'div' );
		const adHeading = document.createElement( 'p' );
		adHeading.className =
			'text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2';
		adHeading.textContent = __( 'Administrations', 'anacrm' );
		adContainer.appendChild( adHeading );
		body.appendChild( adContainer );

		initMembershipSection( orgContainer, personId, isNew, { type: 'org' } );
		initMembershipSection( adContainer, personId, isNew, {
			type: 'administration',
		} );
	}

	// Pods renders visible fields via React after DOMContentLoaded.
	// Use a debounced MutationObserver for async renders (edit form) and
	// an immediate attempt for synchronous renders (new-item form).
	let debounceTimer = null;
	const observer = new MutationObserver( () => {
		clearTimeout( debounceTimer );
		debounceTimer = setTimeout( tryBuild, 300 );
	} );

	observer.observe( form, { childList: true, subtree: true } );

	// Try immediately in case fields are already present in the DOM.
	tryBuild();
} );
